// Tenantra Inc. - Careers: candidate portal
import { useEffect, useState } from 'react';
import api from '@/services/api';
import { logout } from '@/services/auth';
import { formatDate } from '@/utils/date';
import { CareersLayout, StageDot, STAGE_LABELS } from './CareersLayout';

const ORDER = ['submitted', 'in_review', 'interview', 'offer', 'hired'];

export default function CandidatePortalPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/careers/me/applications').then(r => setData(r.data)).catch(() => setError(true));
  }, []);

  const signOut = () => { logout(); window.location.href = '/careers'; };

  return (
    <CareersLayout right={
      <button onClick={signOut} className="text-slate-600 hover:text-slate-900" data-testid="candidate-logout-btn">Sign out</button>
    }>
      <div className="max-w-3xl">
        <h1 className="font-archivo text-2xl sm:text-3xl font-bold text-slate-900 mb-1" data-testid="portal-heading">My applications</h1>
        {data?.candidate && <p className="text-sm text-slate-500 mb-8" data-testid="portal-candidate-email">{data.candidate.full_name} · {data.candidate.email}</p>}

        {error ? (
          <p className="text-sm text-red-600" data-testid="portal-error">Could not load your applications. Please sign in again.</p>
        ) : !data ? (
          <div className="h-32 border border-slate-200 bg-slate-50 animate-pulse" />
        ) : data.applications.length === 0 ? (
          <div className="border border-slate-200 px-5 py-8 text-sm text-slate-500" data-testid="portal-empty">
            You have not submitted any applications. <a href="/careers" className="text-teal-700 hover:underline">See open positions</a>
          </div>
        ) : (
          <div className="space-y-6" data-testid="portal-application-list">
            {data.applications.map(app => <ApplicationCard key={app.id} app={app} />)}
          </div>
        )}
      </div>
    </CareersLayout>
  );
}

function ApplicationCard({ app }) {
  const idx = ORDER.indexOf(app.stage);
  const rejected = app.stage === 'rejected';
  const upcoming = app.interviews?.filter(i => new Date(i.scheduled_at) > new Date()) || [];

  return (
    <div className="border border-slate-200" data-testid={`application-card-${app.id}`}>
      <div className="px-5 py-4 flex items-start justify-between gap-4 border-b border-slate-200">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{app.job_title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Submitted {formatDate(app.submitted_at)}</p>
        </div>
        <StageDot stage={app.stage} />
      </div>

      {!rejected && (
        <ol className="px-5 py-4 grid grid-cols-5 gap-1 border-b border-slate-200" data-testid="application-progress">
          {ORDER.map((s, i) => (
            <li key={s} className="text-center">
              <div className={`h-1 mb-2 ${i <= idx ? 'bg-teal-600' : 'bg-slate-200'}`}></div>
              <span className={`text-[10px] ${i <= idx ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>{STAGE_LABELS[s]}</span>
            </li>
          ))}
        </ol>
      )}

      {upcoming.length > 0 && (
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50" data-testid="application-interviews">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Upcoming interview</p>
          {upcoming.map(i => (
            <div key={i.id} className="text-sm text-slate-800">
              <p className="font-medium">{formatDate(i.scheduled_at, true)} · {i.duration_minutes} min</p>
              <a href={i.meeting_link} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline break-all text-xs" data-testid="interview-join-link">{i.meeting_link}</a>
              {i.message && <p className="text-xs text-slate-600 mt-2 leading-relaxed">{i.message}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Activity</p>
        <ul className="space-y-2.5" data-testid="application-timeline">
          {[...app.timeline].reverse().map((t, i) => (
            <li key={i} className="flex gap-3 text-xs">
              <span className="text-slate-400 w-28 flex-shrink-0">{formatDate(t.at, true)}</span>
              <div>
                <span className="text-slate-800 font-medium">{STAGE_LABELS[t.stage] || t.stage}</span>
                {t.note && <p className="text-slate-600 mt-0.5 leading-relaxed">{t.note}</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
